import React, { useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { company, navLinks } from "../../data/siteContent";
import PrimaryButton from "../ui/PrimaryButton";

const LOGO_FULL = `${process.env.PUBLIC_URL}${company.logos.full}`;
const LOGO_ICON = `${process.env.PUBLIC_URL}${company.logos.icon}`;

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const navItem = (link) => (
    <ListItem key={link.path} disablePadding>
      <ListItemButton
        component={RouterLink}
        to={link.path}
        selected={isActive(link.path)}
        sx={{
          borderRadius: 2,
          mx: 1,
          "&.Mui-selected": {
            bgcolor: "primary.light",
            color: "primary.dark",
            fontWeight: 600,
          },
        }}
      >
        <ListItemText primary={link.title} />
      </ListItemButton>
    </ListItem>
  );

  return (
    <>
      <AppBar
        position="sticky"
        color="inherit"
        sx={{
          top: 0,
          bgcolor: scrolled ? "rgba(255,255,255,0.98)" : "#fff",
          backdropFilter: scrolled ? "blur(16px)" : "none",
          borderBottom: "1px solid",
          borderColor: scrolled ? "divider" : "rgba(0,0,0,0.06)",
          boxShadow: scrolled ? "0 4px 24px rgba(12, 21, 40, 0.06)" : "none",
          transition: "all 0.25s ease",
          py: scrolled ? 0 : 0.5,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 80 }, gap: 2 }}>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                color: "inherit",
                flexGrow: { xs: 1, md: 0 },
                minWidth: 0,
              }}
            >
              <Box
                component="img"
                src={LOGO_FULL}
                alt={company.name}
                sx={{
                  height: { md: 56 },
                  maxWidth: { md: 280 },
                  width: "auto",
                  objectFit: "contain",
                  display: { xs: "none", md: "block" },
                }}
              />
              <Box
                component="img"
                src={LOGO_ICON}
                alt={company.name}
                sx={{
                  height: 44,
                  width: "auto",
                  objectFit: "contain",
                  display: { xs: "block", md: "none" },
                }}
              />
            </Box>

            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", gap: 0.5 }}>
                {navLinks.map((link) => (
                  <Typography
                    key={link.path}
                    component={RouterLink}
                    to={link.path}
                    sx={{
                      px: 2,
                      py: 1,
                      textDecoration: "none",
                      color: isActive(link.path) ? "primary.main" : "text.primary",
                      fontWeight: isActive(link.path) ? 700 : 500,
                      borderRadius: 2,
                      "&:hover": { bgcolor: "primary.light", color: "primary.dark" },
                    }}
                  >
                    {link.title}
                  </Typography>
                ))}
              </Box>
            )}

            {!isMobile && (
              <PrimaryButton to="/contact" color="secondary">
                Contact us
              </PrimaryButton>
            )}

            {isMobile && (
              <IconButton
                edge="end"
                aria-label="Open menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "primary.dark" } }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Box
            component="img"
            src={LOGO_FULL}
            alt={company.name}
            sx={{ height: 48, width: "auto", maxWidth: "100%", objectFit: "contain", mb: 1 }}
          />
          <IconButton
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <List>{navLinks.map(navItem)}</List>
        <Box sx={{ p: 2 }}>
          <PrimaryButton to="/contact" fullWidth color="secondary">
            Contact us
          </PrimaryButton>
        </Box>
      </Drawer>
    </>
  );
}
